// One real-world expense, one expense line (audit theme K).
//
// Pure rules only -- no imports -- so tests load this file as-is. The tax
// cron (api/_tax-bill-reminders-impl.js) mirrors the escrow rule.
//
// REPAIRS (posted by the RPCs in migration 20260928151000, which serialise on
// the work order so concurrent requests cannot double-post):
//   - completing a work order accrues DR Repairs / CR Accounts Payable once,
//     reference WO-<wo id>, minus anything linked invoice payments already
//     expensed;
//   - paying an invoice LINKED to a work order first clears whatever the
//     accrual has left (DR Accounts Payable); only an excess is new expense.
//     Reference VPAY-<invoice id>;
//   - an invoice with no work order books DR Repairs / CR Checking when paid
//     (owner decision: vendor bills are booked when paid);
//   - once the work order is completed and every linked, non-archived invoice
//     is paid, the unused accrual is reversed once (DR Accounts Payable /
//     CR Repairs, reference WO-ADJ-<wo id>). NOTE: it fires as soon as every
//     invoice entered SO FAR is paid -- an invoice entered later is booked as
//     new expense, so the total is right, but P&L is understated in between;
//   - voided entries count as absent everywhere, so a voided accrual or
//     close-out is posted again on the next completion / payment.
//
// MORTGAGE. Owner decision: cash basis, no automatic mortgage entries. The
// wizard creates no recurring mortgage schedule and the Loans page's "Record
// payment" posts nothing -- it only tracks the balance. The payment is booked
// when its bank transaction is categorized or matched in Banking.
//
// ESCROWED TAXES. When the lender pays the taxes from escrow there is no tax
// bill for the owner to pay, so none is generated, listed as due or reminded.
// escrow_covers is free-form, so it is read CONSERVATIVELY: anything unclear
// or negated counts as NOT escrowed -- a bill that shows up is safer than a
// tax payment nobody makes.

export function workOrderAccrualReference(woId) {
  const id = String(woId ?? "").trim();
  return id ? "WO-" + id : null;
}
export function workOrderCloseoutReference(woId) {
  const id = String(woId ?? "").trim();
  return id ? "WO-ADJ-" + id : null;
}
export function invoicePaymentReference(invoiceId) {
  const id = String(invoiceId ?? "").trim();
  return id ? "VPAY-" + id : null;
}

// ─── escrowed taxes ────────────────────────────────────────────────────────
// "Property_Taxes" / "property-tax" / " TAXES " -> "property taxes" etc.
// MIRRORED in api/_tax-bill-reminders-impl.js; both are held to
// tests/fixtures/escrow-covers.json.
const norm = (v) => String(v ?? "").toLowerCase().replace(/[_\-./]+/g, " ").replace(/\s+/g, " ").trim();
const TAX_TOKENS = new Set(["tax", "taxes", "property tax", "property taxes", "real estate tax", "real estate taxes", "re tax", "re taxes",
  "county tax", "county taxes", "city tax", "city taxes", "school tax", "school taxes",
  "piti", "t&i", "t & i", "t and i", "taxes and insurance", "taxes & insurance", "tax and insurance", "tax & insurance"]);
const isTaxToken = (v) => TAX_TOKENS.has(norm(v));
const TRUTHY = new Set(["true", "yes", "y", "1", "included", "x"]);
const isTruthy = (v) => v === true || v === 1 || TRUTHY.has(norm(v));
// Any hint that taxes are NOT (or no longer, or not certainly) in escrow.
const NEGATION = /\b(no|not|none|without|exclud\w*|except|n a|false|owner pays?|paid by owner|owner paid|self pay\w*|waiv\w*|cancel\w*|remov\w*|separate\w*|borrower|by me|myself|previous\w*|formerly|former|used to|no longer|ended|stopped|dropped|ask|unknown|unsure|tbd|maybe)\b|\?/;
// Names taxes: "tax"/"taxes" as a word, or PITI / T&I.
const NAMES_TAXES = /\btax(es)?\b|\bpiti\b|(^|[^a-z])t\s*(&|and)\s*i([^a-z]|$)/;

// Free text: escrowed only when it names taxes and carries no negation or
// doubt, and "X only" does not exclude them ("Insurance only" -> no).
function textCoversTaxes(text) {
  const t = norm(text);
  if (!t || NEGATION.test(t)) return false;
  if (!NAMES_TAXES.test(t)) return false;
  const only = t.match(/^(.*?)\bonly\b/);
  if (only && !NAMES_TAXES.test(only[1])) return false;
  return true;
}

// escrow_covers is the object {taxes, insurance, pmi} since parity C6
// (2026-10-18; migration 20261018030000 backfilled and CHECKs it). This
// reader stays tolerant of what the column held before -- an array of tokens
// (["taxes"], the old column default shape) or free text (the Loans page's
// old input) -- so a cron or report on an unmigrated copy still reads right.
export function escrowCoversTaxes(covers) {
  if (covers == null || covers === false) return false;
  if (typeof covers === "string") return textCoversTaxes(covers);
  if (Array.isArray(covers)) return covers.some(c => isTaxToken(c));
  if (typeof covers === "object") return Object.entries(covers).some(([k, v]) => isTaxToken(k) && isTruthy(v));
  return false;
}

// ─── parseEscrowCovers (parity C6, 2026-10-18) ─────────────────────────────
// Whatever the column (or a form) holds -> {taxes, insurance, pmi}, booleans.
// MIRRORED in SQL as public._wizard_escrow_parse (migration 20261018030000),
// which backfilled the column with it; tests/fixtures/escrow-covers.json
// holds both to the same answers.
//   - an object: a key that names the item, with a truthy value, as
//     escrowCoversTaxes reads it ({taxes:true}, {Taxes:"yes"}, {property_tax:1})
//   - an array of tokens: each token names an item ("Property Taxes", "PITI")
//   - text: taxes exactly as textCoversTaxes (conservative: any negation or
//     doubt anywhere -> not escrowed); insurance and PMI by clause, so
//     "Insurance only (owner pays taxes)" is insurance yes, taxes no. "X only"
//     excludes what X does not name.
const NAMES_INS = /\binsurance\b|\bins\b|\bhazard\b|\bhoi\b|\bpiti\b|(^|[^a-z])t\s*(&|and)\s*i([^a-z]|$)/;
const NAMES_PMI = /\bpmi\b|\bmip\b/;
// "mortgage insurance" is PMI, not the hazard policy.
const pmiNorm = (t) => t.replace(/\b(private )?mortgage insurance\b/g, "pmi");
const isInsToken = (v) => NAMES_INS.test(pmiNorm(norm(v)));
const isPmiToken = (v) => NAMES_PMI.test(pmiNorm(norm(v)));
// Clause rule for one item: named in some clause that carries no negation,
// and in no clause that does; "X only" with X not naming it -> no.
function clauseCovers(text, names) {
  // "T&I" / "T and I" would be split apart by the clause rule below.
  const t = pmiNorm(norm(text)).replace(/(^|[^a-z])t\s*(&|and)\s*i([^a-z]|$)/g, "$1taxes insurance$3");
  if (!t) return false;
  const only = t.match(/^(.*?)\bonly\b/);
  if (only && !names.test(only[1])) return false;
  const clauses = t.split(/[,;()[\]|+&]+|\band\b/).map(c => c.trim()).filter(Boolean);
  const yes = clauses.some(c => names.test(c) && !NEGATION.test(c));
  const no = clauses.some(c => names.test(c) && NEGATION.test(c));
  return yes && !no;
}
export function parseEscrowCovers(covers) {
  const none = { taxes: false, insurance: false, pmi: false };
  if (covers == null || covers === false || covers === true) return none;
  if (typeof covers === "string") {
    return { taxes: textCoversTaxes(covers), insurance: clauseCovers(covers, NAMES_INS), pmi: clauseCovers(covers, NAMES_PMI) };
  }
  if (Array.isArray(covers)) {
    const toks = covers.filter(c => typeof c === "string" || typeof c === "number");
    return { taxes: toks.some(isTaxToken), insurance: toks.some(isInsToken), pmi: toks.some(isPmiToken) };
  }
  if (typeof covers === "object") {
    const on = (names) => Object.entries(covers).some(([k, v]) => names(k) && isTruthy(v));
    return { taxes: on(isTaxToken), insurance: on(isInsToken), pmi: on(isPmiToken) };
  }
  return none;
}

// Taxes are escrowed when the tax record says the lender pays them, or an
// active loan on the property includes escrow that covers taxes. (Portfolio
// loans record escrow_included but not WHAT it covers, so they never count.)
export function taxesEscrowed({ taxRecord, loans }) {
  if (taxRecord && taxRecord.escrow_paid_by_lender === true) return true;
  return (loans || []).some(l => l && !l.archived_at && l.status !== "paid_off" && l.escrow_included === true && escrowCoversTaxes(l.escrow_covers));
}
